import React, { useState, useEffect, useCallback } from 'react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaImage,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaCloudUploadAlt
} from 'react-icons/fa';
import { getData, postData, putData } from '../../../../services/Fetchnodeservices';

const RestaurantOfferBanner = ({ isDark = false }) => {
  // ── State ──
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');

  // ── Theme ──
  const theme = {
    bg: isDark ? '#0f172a' : '#f1f5f9',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#475569',
    textMuted: isDark ? '#64748b' : '#94a3b8',
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    hoverBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    primary: '#3b82f6',
    primaryDark: '#2563eb',
    success: '#22c55e',
    danger: '#ef4444',
    warning: '#f59e0b',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
  };

  // ── API Base URL ──
  const API_BASE = 'RestaurantOfferBanne';
  const isSuccess = (result) => result?.success === true || result?.status === true;

  // ── Fetch Banners ──
  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getData(`${API_BASE}/restaurantOfferBanner`);
      // Handle the response structure: {status: true, message: 'success', data: [...]}
      if (isSuccess(result)) {
        setBanners(result.data || []);
      } else {
        alert('Failed to fetch banners: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      alert('Error fetching banners');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Initial Load ──
  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // ── Handle Image Selection ──
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ── Remove Image ──
  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // ── Open Create Modal ──
  const openCreateModal = () => {
    setEditingBanner(null);
    setSelectedImage(null);
    setImagePreview(null);
    setShowModal(true);
  };

  // ── Open Edit Modal ──
  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setSelectedImage(null);
    setImagePreview(null);
    setShowModal(true);
  };

  // ── Close Modal ──
  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    setSelectedImage(null);
    setImagePreview(null);
  };

  // ── Create Banner ──
  const createBanner = async () => {
    if (!selectedImage) {
      alert('Please select an image');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);

      const result = await postData(`${API_BASE}/restaurantOfferBanner/create`, formData);

      if (result.status === true) {
        alert('✅ Banner added successfully!');
        closeModal();
        fetchBanners();
      } else {
        alert('❌ Failed to add banner: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating banner:', error);
      alert('❌ Error creating banner');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Update Banner ──
  const updateBanner = async () => {
    if (!editingBanner) return;
    if (!selectedImage) {
      alert('Please select a new image before updating the banner.');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const result = await putData(
        `${API_BASE}/restaurantOfferBanner/update/${editingBanner.id}`,
        formData
      );

      if (isSuccess(result)) {
        alert('✅ Banner updated successfully!');
        closeModal();
        fetchBanners();
      } else {
        alert('❌ Failed to update banner: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating banner:', error);
      alert('❌ Error updating banner');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete Banner ──
  const deleteBanner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      const result = await postData(`${API_BASE}/restaurantOfferBanner/delete/${id}`);
      if (isSuccess(result)) {
        alert('✅ Banner deleted successfully!');
        fetchBanners();
      } else {
        alert('❌ Failed to delete banner: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      alert('❌ Error deleting banner');
    }
  };

  // ── Toggle Status ──
  const toggleStatus = async (id) => {
    try {
      const result = await postData(`${API_BASE}/restaurantOfferBanner/status/${id}`, {});
      if (isSuccess(result)) {
        alert('✅ Status updated successfully!');
        fetchBanners();
      } else {
        alert('❌ Failed to update status: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('❌ Error updating status');
    }
  };

  // ── Handle Submit ──
  const handleSubmit = () => {
    if (editingBanner) {
      updateBanner();
    } else {
      createBanner();
    }
  };

  // ── Filter and Sort Banners ──
  const getFilteredBanners = () => {
    let filtered = [...banners];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(banner =>
        banner.id.toString().includes(query) ||
        (banner.image && banner.image.toLowerCase().includes(query))
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  // ── Pagination ──
  const getPaginatedData = () => {
    const filtered = getFilteredBanners();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      data: filtered.slice(startIndex, endIndex),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage)
    };
  };

  // ── Handle Sort ──
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // ── Get Sort Icon ──
  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ fontSize: '12px', opacity: 0.3 }} />;
    return sortOrder === 'asc' ? 
      <FaSortUp style={{ fontSize: '12px', color: theme.primary }} /> : 
      <FaSortDown style={{ fontSize: '12px', color: theme.primary }} />;
  };

  // ── Format Date ──
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // ── Get Image URL ──
  const getImageUrl = (imageName) => {
    // Replace with your actual uploads URL
    return `${process.env.REACT_APP_API_URL || 'https://new.zaptodoor.com'}/uploads/${imageName}`;
  };

  // ── Styles ──
  const styles = {
    container: {
      backgroundColor: theme.bg,
      minHeight: '100vh',
      padding: '24px',
      fontFamily: "'Inter', -apple-system, sans-serif",
      color: theme.text,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '16px',
      marginBottom: '24px',
      padding: '20px 24px',
      background: theme.cardBg,
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
    },
    headerTitle: {
      fontSize: '22px',
      fontWeight: 700,
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerActions: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
    },
    btn: (bg, color) => ({
      padding: '10px 20px',
      borderRadius: '10px',
      border: 'none',
      background: bg || theme.primary,
      color: color || '#fff',
      fontWeight: 600,
      fontSize: '13px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }),
    filters: {
      display: 'flex',
      gap: '12px',
      marginBottom: '20px',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    searchInput: {
      padding: '10px 16px 10px 40px',
      borderRadius: '10px',
      border: `1px solid ${theme.inputBorder}`,
      backgroundColor: theme.inputBg,
      color: theme.text,
      fontSize: '14px',
      flex: 1,
      minWidth: '200px',
      outline: 'none',
      transition: 'all 0.2s ease',
    },
    card: {
      background: theme.cardBg,
      borderRadius: '12px',
      padding: '16px 20px',
      border: `1px solid ${theme.border}`,
      marginBottom: '12px',
      transition: 'all 0.2s ease',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '12px',
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: `2px solid ${theme.border}`,
      color: theme.textSecondary,
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'all 0.2s ease',
    },
    td: {
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.border}`,
      color: theme.text,
      fontSize: '13px',
    },
    imageThumb: {
      width: '50px',
      height: '50px',
      borderRadius: '8px',
      objectFit: 'cover',
      border: `1px solid ${theme.border}`,
    },
    statusBadge: (active) => ({
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: active ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
      color: active ? '#22c55e' : '#ef4444',
    }),
    actionBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '6px',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
      color: theme.textSecondary,
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    modalContent: {
      background: theme.cardBg,
      borderRadius: '16px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      padding: '30px',
      border: `1px solid ${theme.border}`,
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      position: 'relative',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: 700,
      color: theme.text,
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: theme.textSecondary,
      transition: 'all 0.2s ease',
    },
    uploadArea: {
      border: `2px dashed ${theme.border}`,
      borderRadius: '12px',
      padding: '40px 20px',
      textAlign: 'center',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      position: 'relative',
    },
    uploadIcon: {
      fontSize: '48px',
      color: theme.textMuted,
      marginBottom: '12px',
    },
    uploadText: {
      color: theme.textSecondary,
      fontSize: '14px',
    },
    imagePreview: {
      width: '100%',
      maxHeight: '300px',
      objectFit: 'contain',
      borderRadius: '8px',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '20px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    paginationBtn: (disabled) => ({
      padding: '8px 16px',
      borderRadius: '8px',
      border: `1px solid ${theme.border}`,
      background: theme.cardBg,
      color: disabled ? theme.textMuted : theme.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
      opacity: disabled ? 0.5 : 1,
    }),
    paginationInfo: {
      color: theme.textSecondary,
      fontSize: '13px',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: '16px',
    },
    spinner: {
      fontSize: '32px',
      color: theme.primary,
      animation: 'spin 0.8s linear infinite',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: theme.textSecondary,
    },
    removeImageBtn: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      background: theme.danger,
      color: '#fff',
      border: 'none',
      borderRadius: '50%',
      width: '28px',
      height: '28px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  // ── Loading ──
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <FaSpinner style={styles.spinner} />
        <p style={{ color: theme.textSecondary }}>Loading banners...</p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const { data: paginatedData, total, totalPages } = getPaginatedData();

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <FaImage style={{ color: theme.primary }} />
          Offer Banners
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            background: 'rgba(59, 130, 246, 0.15)',
            color: '#60a5fa',
          }}>
            {banners.length} Banners
          </span>
        </div>
        <div style={styles.headerActions}>
          <button
            style={styles.btn(theme.gradient, '#fff')}
            onClick={openCreateModal}
          >
            <FaPlus /> Add Banner
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={styles.filters}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FaSearch style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted }} />
          <input
            type="text"
            placeholder="Search banners..."
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div style={styles.card}>
        {paginatedData.length === 0 ? (
          <div style={styles.emptyState}>
            <FaImage size={48} style={{ color: theme.textMuted, marginBottom: '12px' }} />
            <p>No banners found. Add your first offer banner!</p>
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('id')}>
                    ID {getSortIcon('id')}
                  </th>
                  <th style={styles.th}>Image</th>
                  <th style={styles.th} onClick={() => handleSort('status')}>
                    Status {getSortIcon('status')}
                  </th>
                  <th style={styles.th} onClick={() => handleSort('created_at')}>
                    Created {getSortIcon('created_at')}
                  </th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((banner) => (
                  <tr key={banner.id} style={{ animation: 'fadeIn 0.3s ease' }}>
                    <td style={styles.td}>
                      <span style={{ fontWeight: 600 }}>#{banner.id}</span>
                    </td>
                    <td style={styles.td}>
                      <img
                        src={getImageUrl(banner.image)}
                        alt={`Banner ${banner.id}`}
                        style={styles.imageThumb}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"/%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"/%3E%3Cpath d="M21 15l-5-5L5 21"/%3E%3C/svg%3E';
                          e.target.style.objectFit = 'contain';
                          e.target.style.padding = '8px';
                        }}
                      />
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statusBadge(banner.status === 1)}>
                        {banner.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {formatDate(banner.created_at)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button
                        style={styles.actionBtn}
                        onClick={() => toggleStatus(banner.id)}
                        title={banner.status === 1 ? 'Deactivate' : 'Activate'}
                      >
                        {banner.status === 1 ? 
                          <FaToggleOn style={{ color: theme.success, fontSize: '22px' }} /> : 
                          <FaToggleOff style={{ color: theme.textMuted, fontSize: '22px' }} />
                        }
                      </button>
                      <button
                        style={styles.actionBtn}
                        onClick={() => openEditModal(banner)}
                        title="Edit"
                      >
                        <FaEdit style={{ color: theme.primary }} />
                      </button>
                      <button
                        style={styles.actionBtn}
                        onClick={() => deleteBanner(banner.id)}
                        title="Delete"
                      >
                        <FaTrash style={{ color: theme.danger }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Pagination ── */}
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, total)} of {total} banners
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={styles.paginationBtn(currentPage === 1)}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft />
                </button>
                <span style={{ padding: '8px 16px', color: theme.textSecondary }}>
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  style={styles.paginationBtn(currentPage === totalPages || totalPages === 0)}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {editingBanner ? 'Edit Banner' : 'Add New Banner'}
              </h3>
              <button style={styles.closeBtn} onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {/* Image Upload */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>
                Banner Image {!editingBanner && <span style={{ color: theme.danger }}>*</span>}
              </label>
              {imagePreview || (editingBanner && !selectedImage) ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={imagePreview || getImageUrl(editingBanner?.image)}
                    alt="Preview"
                    style={styles.imagePreview}
                  />
                  <button
                    style={styles.removeImageBtn}
                    onClick={removeImage}
                    title="Remove image"
                  >
                    <FaTimes size={14} />
                  </button>
                  {editingBanner && !selectedImage && (
                    <button
                      type="button"
                      style={{
                        position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
                        border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer',
                        background: theme.primary, color: '#fff', fontWeight: 600,
                      }}
                      onClick={() => document.getElementById('imageInput').click()}
                    >
                      Change image
                    </button>
                  )}
                  <input
                    id="imageInput"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                  />
                </div>
              ) : (
                <div
                  style={styles.uploadArea}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.background = theme.hoverBg;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.background = 'transparent';
                  }}
                  onClick={() => document.getElementById('imageInput').click()}
                >
                  <FaCloudUploadAlt style={styles.uploadIcon} />
                  <p style={styles.uploadText}>
                    Click to upload or drag and drop<br />
                    <span style={{ fontSize: '12px', color: theme.textMuted }}>
                      PNG, JPG, JPEG (Max 5MB)
                    </span>
                  </p>
                  <input
                    id="imageInput"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                  />
                </div>
              )}
              {selectedImage && (
                <p style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '8px' }}>
                  Selected: {selectedImage.name}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                style={styles.btn('transparent', theme.text)}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                style={styles.btn(theme.gradient, '#fff')}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} /> : <FaSave />}
                {submitting ? 'Saving...' : (editingBanner ? 'Update' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantOfferBanner;
